import { UPLIFT_FLAG_KEYS, hasUpliftEnabled, isMultiYearDuration } from '@vy/types';
import { isNumber } from '@vy/utils';
import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { userMessage } from '../../../../utils/vy-toasts';
import type { NewPlanForm } from '../create-plan-page-components/CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';

const PRODUCT_ARRAYS = ['fixedProducts', 'usageProducts', 'promotionProducts'] as const;

/**
 * Uplift requires a multi-year plan (13+ months). The switches are disabled
 * while the duration is short, but a user can enable uplift first and lower
 * the duration after — so when the duration drops below the threshold, turn
 * the uplift flags off and tell the user why.
 */
export function useClearUpliftOnShortDuration(form: UseFormReturn<PlanInputForm, unknown, NewPlanForm>) {
  const { watch, getValues, setValue } = form;

  useEffect(() => {
    const subscription = watch((values, { name }) => {
      // Only react to a real edit of the billing duration. RHF also fires this
      // subscription with `name === undefined` on initial subscribe, on reset(),
      // and on form-persist restore — acting on those would spuriously clear
      // uplift flags (and toast) for state the user never touched this session.
      if (!name || !name.startsWith('billingData')) {
        return;
      }

      const duration = values.billingData?.duration;

      if (!isNumber(duration) || isMultiYearDuration(duration)) {
        return;
      }

      let cleared = false;

      PRODUCT_ARRAYS.forEach((arrayName) => {
        getValues(arrayName)?.forEach((product, productIndex) => {
          if (!hasUpliftEnabled(product)) {
            return;
          }

          cleared = true;

          UPLIFT_FLAG_KEYS.forEach((flag) => {
            if (product.upliftConfiguration?.[flag]) {
              setValue(`${arrayName}.${productIndex}.upliftConfiguration.${flag}`, false, {
                shouldDirty: true,
              });
            }
          });
        });
      });

      if (cleared) {
        userMessage('warning', 'Uplift was turned off — it is only available on multi-year contracts (13+ months)');
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, getValues, setValue]);
}
